"use client";
import { MAX_HEARTBEAT_TIMEOUT } from "@klarify/constants";
import {
	durationToMilliseconds,
	LocalStorageMap,
} from "@klarify/utilities/client";
import { isAfter, sub } from "date-fns";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useReloadProtectionOptional } from "../reload-handler";
import { isTabActive, useTabId } from "../tab-identifier";
import {
	hasRecordingActivity,
	subscribeRecordingActivity,
	tabSessions,
} from "./global";

interface ActiveRecordingInfo {
	lastActive: Date;
	owningTabId: string;
}

const activeRecordings = new LocalStorageMap<string, ActiveRecordingInfo>(
	"activeRecordingsV3",
);

/**
 * Check whether the session is actively being recorded on any tab.
 * @param sessionId The session ID.
 * @returns Whether the session is actively being recorded.
 */
export function isActiveRecording(sessionId: string): boolean {
	if (tabSessions.has(sessionId)) {
		return true;
	}
	const lastActive = activeRecordings.get(sessionId);
	if (!lastActive) {
		return false;
	}
	if (!isTabActive(lastActive.owningTabId)) {
		activeRecordings.delete(sessionId);
		return false;
	}
	const lastAllowableHeartbeatDate = sub(new Date(), MAX_HEARTBEAT_TIMEOUT);
	if (isAfter(lastActive.lastActive, lastAllowableHeartbeatDate)) {
		return true;
	}
	activeRecordings.delete(sessionId);
	return false;
}

/**
 * Drop a session's activity marker when `tabId` owns it, regardless of how
 * fresh it looks. For the adoption fast-path only: a marker written moments
 * before its tab died stays "fresh" for up to `MAX_HEARTBEAT_TIMEOUT`, and the
 * resumed incarnation — alive, same id — would read its dead predecessor's
 * marker as its own live recording and refuse to repair the session.
 */
export function clearOwnedActivityMarker(sessionId: string, tabId: string) {
	const marker = activeRecordings.get(sessionId);
	if (marker && marker.owningTabId === tabId) {
		activeRecordings.delete(sessionId);
	}
}

/**
 * Hook that provides functions to mark a session as active or inactive.
 * @returns Functions to mark a session as active or inactive.
 */
export function useActivityMarker() {
	const tabId = useTabId();

	// Empty string starts check on first render
	const lastTabId = useRef<string>("");

	/**
	 * Remap active recordings to new tab if tab id changes
	 */
	useEffect(() => {
		// If mismatch (including first render), remap
		if (lastTabId.current !== tabId) {
			activeRecordings.forEach((value, key) => {
				// Only remap if this tab owned it previously
				if (value.owningTabId === lastTabId.current) {
					// Update to new tab id
					activeRecordings.set(key, {
						...value,
						owningTabId: tabId,
					});
				}
			});
			// Update last tab id
			lastTabId.current = tabId;
		}
	}, [tabId]);

	/**
	 * Mark a session as active.
	 * @param sessionId The session ID.
	 */
	const markActive = useCallback(
		(sessionId?: string | null) => {
			if (!sessionId) {
				return;
			}
			const old = activeRecordings.get(sessionId);
			if (old && old.owningTabId !== tabId) {
				// Another tab is marking this active; do not override
				return;
			}
			activeRecordings.set(sessionId, {
				lastActive: new Date(),
				owningTabId: tabId,
			});
		},
		[tabId],
	);
	/**
	 * Mark a session as inactive.
	 * @param sessionId The session ID.
	 */
	const markInactive = useCallback(
		(sessionId?: string | null) => {
			if (!sessionId) {
				return;
			}
			const recording = activeRecordings.get(sessionId);
			if (recording && recording.owningTabId === tabId) {
				activeRecordings.delete(sessionId);
			}
		},
		[tabId],
	);

	return { markActive, markInactive };
}

/**
 * Hook that returns whether a session is actively being recorded.
 * @param sessionId The session ID.
 * @returns Whether the session is actively being recorded.
 */
export function useActivity(sessionId: string) {
	const subscribe = useCallback(
		(callback: () => void) => {
			// Poll for heartbeat timeout expiry (time-based, not event-driven)
			const intv = setInterval(
				callback,
				durationToMilliseconds(MAX_HEARTBEAT_TIMEOUT) / 4,
			);
			const onEvent = (event: { key: string }) => {
				if (event.key === sessionId) {
					callback();
				}
			};
			const addedOff = activeRecordings.on("added", ({ data }) =>
				onEvent(data),
			);
			const deletedOff = activeRecordings.on("removed", ({ data }) =>
				onEvent(data),
			);
			const updatedOff = activeRecordings.on("updated", ({ data }) =>
				onEvent(data),
			);
			return () => {
				clearInterval(intv);
				addedOff();
				deletedOff();
				updatedOff();
			};
		},
		[sessionId],
	);

	return useSyncExternalStore(
		subscribe,
		() => isActiveRecording(sessionId),
		() => false,
	);
}

/**
 * Hook that returns whether any session is recording or starting on this tab.
 * Starting counts: live tracks can exist before tabSessions registers the
 * session, and consumers must not disturb that window.
 */
export function useHasActiveRecording() {
	const subscribe = useCallback((callback: () => void) => {
		const addedOff = activeRecordings.on("added", callback);
		const deletedOff = activeRecordings.on("removed", callback);
		const updatedOff = activeRecordings.on("updated", callback);
		// The snapshot reads `tabSessions` and the starting tokens, neither of
		// which has events of its own — the activity channel notifies on those
		// mutations directly, where subscribing to `activeRecordings` alone only
		// worked as a side effect of the recorder's 500 ms marker interval.
		const activityOff = subscribeRecordingActivity(callback);
		return () => {
			addedOff();
			deletedOff();
			updatedOff();
			activityOff();
		};
	}, []);

	return useSyncExternalStore(subscribe, hasRecordingActivity, () => false);
}

/**
 * Root-level reload protection for live recordings. The StreamingUploadManager
 * lives outside React and keeps capturing when a page-owned recorder hook
 * unmounts (viewport-variant swap, navigation via an unprotected router). The
 * hook-level block releases on unmount by design — so without this holder a
 * queued deployment reload fires the moment the semaphore hits zero and kills
 * the recording. Reload-only: navigation blocking stays with the mounted
 * recorder hooks. Mount once above the routes (RecordingHost).
 */
export function useActiveRecordingReloadProtection() {
	const hasActiveRecording = useHasActiveRecording();
	const protection = useReloadProtectionOptional();
	const protect = protection?.protect;

	useEffect(() => {
		if (!(hasActiveRecording && protect)) {
			return;
		}
		const unprotect = protect({ blockNavigation: false });
		return unprotect;
	}, [hasActiveRecording, protect]);
}
